// Reliability, Brier, and the two recalibration maps — the arithmetic behind "calibrated on N fights".
//
// This module is deliberately free of any Jev, sim or React import: it is pure numbers, so the same
// code runs in three places that must agree with each other or the badge is a lie.
//
//   1. `tools/jev-calibrate.mts`, offline, to fit a map from thousands of labelled sim samples;
//   2. `src/ai/calibration.ts`, in the browser, to apply that map to a live estimate;
//   3. `src/ui/debrief/Calibration.tsx`, after a run, to score the *player's* forecasts the same way
//      the estimator's are scored, because the whole point of the debrief panel is that both are
//      measured with one ruler.
//
// Why recalibration at all: Jev's own documentation says confidence "derives from the probability
// distribution" and tells you to "test with your own data". The published independent work agrees and
// is more specific — in-distribution expected calibration error of 0.117-0.160 falling to 0.008-0.052
// after Platt or isotonic recalibration (AnthusAI/Jev-Calibration), and an out-of-distribution error
// of 0.107 whose *sign flips by question type*: choice and score overconfident, boolean underconfident
// (scienthoon/jev-ood-calibration). A single global fudge factor would therefore make one of our three
// questions worse. Hence one map per question per provider, fitted on this simulation's own labels.
// See docs/research/05-jev-evaluation.md.

/** One row of a reliability table: what was promised in this probability band, and what happened. */
export interface ReliabilityBin {
  /** Half-open band [lo, hi), except the last which is closed. */
  lo: number;
  hi: number;
  n: number;
  /** Mean forecast probability of the samples that fell in this bin. */
  meanPredicted: number;
  /** Fraction of those samples whose event actually occurred. Perfect calibration: equals meanPredicted. */
  meanOutcome: number;
}

/** One forecast and what happened: `p` is the probability that was asserted, `outcome` is 1 or 0. */
export interface ForecastPoint { p: number; outcome: 0 | 1 }

export const DEFAULT_BINS = 10;

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/** Groups forecasts into `bins` equal-width probability bands. Empty bands are kept, with `n: 0`, so a
 * reliability strip always has the same number of columns and two runs can be laid side by side. */
export function reliabilityBins(points: readonly ForecastPoint[], bins: number = DEFAULT_BINS): ReliabilityBin[] {
  const out: ReliabilityBin[] = [];
  const sums = Array.from({ length: bins }, () => ({ n: 0, p: 0, o: 0 }));
  for (const pt of points) {
    const p = clamp01(pt.p);
    // `Math.min` rather than a special case for p === 1: the top band is the closed one.
    const i = Math.min(bins - 1, Math.floor(p * bins));
    sums[i].n += 1;
    sums[i].p += p;
    sums[i].o += pt.outcome;
  }
  for (let i = 0; i < bins; i++) {
    const s = sums[i];
    out.push({
      lo: i / bins,
      hi: (i + 1) / bins,
      n: s.n,
      meanPredicted: s.n > 0 ? s.p / s.n : (i + 0.5) / bins,
      meanOutcome: s.n > 0 ? s.o / s.n : 0,
    });
  }
  return out;
}

/**
 * Expected calibration error: the bin-count-weighted mean gap between what was promised and what
 * happened. 0 is perfect; 0.5 is the worst a forecaster can manage while still being asked the question.
 */
export function expectedCalibrationError(bins: readonly ReliabilityBin[]): number {
  let total = 0;
  let weighted = 0;
  for (const b of bins) {
    if (b.n === 0) continue;
    total += b.n;
    weighted += b.n * Math.abs(b.meanOutcome - b.meanPredicted);
  }
  return total > 0 ? weighted / total : 0;
}

/** Brier score for a yes/no forecast: mean squared error of the probability. Lower is better; 0.25 is
 * what you get by always saying 50%, so anything above it is worse than refusing to answer. */
export function brierScore(points: readonly ForecastPoint[]): number {
  if (points.length === 0) return 0;
  let sum = 0;
  for (const pt of points) sum += (clamp01(pt.p) - pt.outcome) ** 2;
  return sum / points.length;
}

/**
 * Multi-class Brier score: the sum over options of (probability - indicator)^2, averaged over samples.
 *
 * Note the scale: this is the *unnormalised* multi-category form, which ranges 0..2 rather than 0..1,
 * so a three-way choice answered with a flat 1/3 scores 0.667 and cannot be compared with the yes/no
 * number above. Kept unnormalised because it is the form the calibration literature and the reference
 * implementations use; the interface always labels which question a score belongs to.
 */
export function brierMulti(
  points: readonly { probabilities: Record<string, number>; actual: string }[],
): number {
  if (points.length === 0) return 0;
  let sum = 0;
  for (const pt of points) {
    for (const [option, p] of Object.entries(pt.probabilities)) {
      const indicator = option === pt.actual ? 1 : 0;
      sum += (p - indicator) ** 2;
    }
  }
  return sum / points.length;
}

// ---- the two maps ---------------------------------------------------------------------------------

/** Platt scaling: a logistic regression on the log-odds of the raw probability. Two parameters, so it
 * cannot overfit a few hundred samples, and it is monotone, so it never reorders two forecasts. */
export interface PlattMap { kind: 'platt'; a: number; b: number }

/** Isotonic regression: the best monotone step function through the samples (pool-adjacent-violators).
 * Strictly more expressive than Platt and strictly more willing to overfit; the harness fits both and
 * keeps whichever has the lower error on the data it did not use to fit. */
export interface IsotonicMap { kind: 'isotonic'; points: { x: number; y: number }[] }

/** No recalibration: what an uncalibrated provider gets, and what every map degrades to. */
export interface IdentityMap { kind: 'identity' }

export type CalibrationMap = PlattMap | IsotonicMap | IdentityMap;

export const IDENTITY_MAP: IdentityMap = { kind: 'identity' };

const EPS = 1e-6;

function logit(p: number): number {
  const q = Math.min(1 - EPS, Math.max(EPS, p));
  return Math.log(q / (1 - q));
}

function sigmoid(z: number): number {
  return 1 / (1 + Math.exp(-z));
}

/**
 * Fits `p' = sigmoid(a * logit(p) + b)` by Newton-Raphson on the log-likelihood.
 *
 * Platt's own correction for small samples is used on the targets (n+ + 1)/(n+ + 2) and 1/(n- + 2)
 * rather than a hard 1 and 0, which is what stops the fit running away to infinite `a` when the raw
 * forecasts happen to separate the classes perfectly.
 */
export function fitPlatt(points: readonly ForecastPoint[]): PlattMap {
  if (points.length < 4) return { kind: 'platt', a: 1, b: 0 };
  let pos = 0;
  for (const pt of points) pos += pt.outcome;
  const neg = points.length - pos;
  if (pos === 0 || neg === 0) return { kind: 'platt', a: 1, b: 0 };
  const hi = (pos + 1) / (pos + 2);
  const lo = 1 / (neg + 2);

  let a = 1;
  let b = 0;
  for (let iter = 0; iter < 100; iter++) {
    let g0 = 0, g1 = 0;          // gradient wrt a, b
    let h00 = 0, h01 = 0, h11 = 0; // Hessian
    for (const pt of points) {
      const x = logit(pt.p);
      const target = pt.outcome === 1 ? hi : lo;
      const q = sigmoid(a * x + b);
      const d = q - target;
      const w = q * (1 - q);
      g0 += d * x;
      g1 += d;
      h00 += w * x * x;
      h01 += w * x;
      h11 += w;
    }
    const det = h00 * h11 - h01 * h01;
    if (!Number.isFinite(det) || Math.abs(det) < 1e-12) break;
    const da = (h11 * g0 - h01 * g1) / det;
    const db = (h00 * g1 - h01 * g0) / det;
    a -= da;
    b -= db;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return { kind: 'platt', a: 1, b: 0 };
    if (Math.abs(da) < 1e-8 && Math.abs(db) < 1e-8) break;
  }
  return { kind: 'platt', a, b };
}

/**
 * Pool-adjacent-violators isotonic regression, returned as the breakpoints of a monotone step
 * function. `apply` linearly interpolates between breakpoints, which keeps it continuous and keeps two
 * nearby forecasts from being mapped to wildly different places.
 */
export function fitIsotonic(points: readonly ForecastPoint[]): IsotonicMap {
  if (points.length === 0) return { kind: 'isotonic', points: [{ x: 0, y: 0 }, { x: 1, y: 1 }] };
  const sorted = [...points].sort((p, q) => p.p - q.p);

  // Each block holds a run of consecutive forecasts that share one fitted value.
  const blocks: { sumY: number; n: number; x: number }[] = [];
  for (const pt of sorted) {
    blocks.push({ sumY: pt.outcome, n: 1, x: clamp01(pt.p) });
    // Merge backwards while the sequence of block means is decreasing (the violation).
    while (blocks.length > 1) {
      const b = blocks[blocks.length - 1];
      const a = blocks[blocks.length - 2];
      if (a.sumY / a.n <= b.sumY / b.n) break;
      blocks.splice(blocks.length - 2, 2, { sumY: a.sumY + b.sumY, n: a.n + b.n, x: b.x });
    }
  }

  const out: { x: number; y: number }[] = [];
  let cursor = 0;
  for (const block of blocks) {
    const y = block.sumY / block.n;
    // Anchor each block at the mean forecast of the samples it pooled, not at its right edge.
    let sumX = 0;
    for (let i = 0; i < block.n; i++) sumX += clamp01(sorted[cursor + i].p);
    cursor += block.n;
    out.push({ x: sumX / block.n, y });
  }
  if (out.length === 0 || out[0].x > 0) out.unshift({ x: 0, y: out[0]?.y ?? 0 });
  if (out[out.length - 1].x < 1) out.push({ x: 1, y: out[out.length - 1].y });
  return { kind: 'isotonic', points: out };
}

/** Runs a fitted map over one probability. Never throws, always returns something in [0, 1]. */
export function applyMap(map: CalibrationMap | undefined, p: number): number {
  const raw = clamp01(p);
  if (!map || map.kind === 'identity') return raw;
  if (map.kind === 'platt') {
    const out = sigmoid(map.a * logit(raw) + map.b);
    return Number.isFinite(out) ? clamp01(out) : raw;
  }
  const pts = map.points;
  if (pts.length === 0) return raw;
  if (raw <= pts[0].x) return clamp01(pts[0].y);
  for (let i = 1; i < pts.length; i++) {
    if (raw <= pts[i].x) {
      const span = pts[i].x - pts[i - 1].x;
      if (span <= 0) return clamp01(pts[i].y);
      const f = (raw - pts[i - 1].x) / span;
      return clamp01(pts[i - 1].y + f * (pts[i].y - pts[i - 1].y));
    }
  }
  return clamp01(pts[pts.length - 1].y);
}

/**
 * Runs a map over a whole categorical distribution and renormalises.
 *
 * This is an approximation and is documented as one: a per-option Platt map recalibrates each option's
 * marginal probability of being right, which is not the same thing as recalibrating the joint
 * distribution, and renormalising afterwards is what keeps it a distribution. It is the standard
 * cheap treatment and it is the one the reference Jev calibration work uses; the alternative (a full
 * Dirichlet or vector-scaling fit) needs far more labelled data than a three-option question earns.
 */
export function calibrateDistribution(
  map: CalibrationMap | undefined,
  probabilities: Record<string, number>,
): Record<string, number> {
  const entries = Object.entries(probabilities);
  if (entries.length === 0) return {};
  const mapped = entries.map(([k, v]) => [k, applyMap(map, v)] as const);
  let total = 0;
  for (const [, v] of mapped) total += v;
  if (!(total > 0)) {
    // Everything mapped to zero: fall back to uniform rather than emitting NaNs.
    const uniform = 1 / entries.length;
    return Object.fromEntries(entries.map(([k]) => [k, uniform]));
  }
  return Object.fromEntries(mapped.map(([k, v]) => [k, v / total]));
}

/**
 * The smallest probability the interface will ever report, and one minus the largest.
 *
 * Isotonic regression is free to map a whole band to exactly 0 or exactly 1, and on real sim data it
 * does: the lowest band of the main-effort fit is flat zero because no sample in it ever came true.
 * That is the correct fit and the wrong thing to report. A probability of 0 is a claim of impossibility,
 * it makes the Brier score unbounded the first time it is wrong, and no S-2 has ever been entitled to
 * one. Half a percent is small enough to read as "no" and large enough to be survivable.
 */
export const PROB_FLOOR = 0.005;

/** Pulls a probability in from the certainties, without changing anything in between. */
export function clampAwayFromCertainty(p: number): number {
  return Math.min(1 - PROB_FLOOR, Math.max(PROB_FLOOR, clamp01(p)));
}

/** The same, for a whole distribution: floor each option, then renormalise. */
export function floorDistribution(probabilities: Record<string, number>): Record<string, number> {
  const entries = Object.entries(probabilities);
  if (entries.length === 0) return {};
  const floored = entries.map(([k, v]) => [k, Math.max(PROB_FLOOR, clamp01(v))] as const);
  const total = floored.reduce((s, [, v]) => s + v, 0);
  return Object.fromEntries(floored.map(([k, v]) => [k, v / total]));
}

/**
 * Jev's own confidence formula, restated so a recalibrated distribution can carry a confidence that
 * matches it: `(count x peak - 1) / (count - 1)`, per https://docs.typesafe.ai/confidence.
 * A one-option question has no spread to measure, so it is defined as 1.
 */
export function confidenceOf(probabilities: Record<string, number>): number {
  const values = Object.values(probabilities);
  if (values.length <= 1) return 1;
  const peak = Math.max(...values);
  return clamp01((values.length * peak - 1) / (values.length - 1));
}

/** The option with the highest probability; ties break on the key, so the answer is deterministic. */
export function argmax(probabilities: Record<string, number>): string {
  let best = '';
  let bestP = -Infinity;
  for (const [k, v] of Object.entries(probabilities)) {
    if (v > bestP || (v === bestP && k < best)) { best = k; bestP = v; }
  }
  return best;
}

// ---- the shape of data/jev-calibration.json --------------------------------------------------------

/** One question, one provider, fitted. */
export interface QuestionCalibration {
  question: string;
  provider: 'heuristic' | 'jev';
  samples: number;
  /** Before the map: what the provider said unprompted. */
  raw: { ece: number; brier: number; bins: ReliabilityBin[] };
  /** After the map, measured on held-out samples the fit never saw. */
  calibrated: { ece: number; brier: number; bins: ReliabilityBin[] };
  /** Which of the two candidate fits won on the held-out half, and by how much. */
  chosen: 'platt' | 'isotonic' | 'identity';
  map: CalibrationMap;
}

export interface CalibrationFile {
  /** ISO timestamp, or null in the placeholder that ships before anyone has run the harness. */
  generatedAt: string | null;
  /** Headless fights the sample set was drawn from. Zero means "no calibration yet". */
  runs: number;
  samples: number;
  aos: string[];
  /** How many Jev calls the run spent, and what they cost at the published input-token price. */
  jevCalls: number;
  estimatedCostUsd: number;
  notes?: string;
  questions: Record<string, { heuristic?: QuestionCalibration; jev?: QuestionCalibration }>;
}

/** True when a calibration file actually holds a fit rather than the shipped placeholder. */
export function calibrationIsPresent(file: CalibrationFile | null | undefined): boolean {
  return Boolean(file && file.runs > 0 && file.generatedAt);
}
